import { useEffect } from "react";

/**
 * Carrega scripts de analytics (GA4 + Ahrefs) apenas após o navegador ficar
 * ocioso ou na primeira interação do usuário. Tira ~50–80KB de JS de
 * terceiros do caminho crítico, melhorando LCP/TTI mobile.
 *
 * Nota: o AdSense foi movido para o <head> fixo em __root.tsx a pedido
 * do operador, carregando imediatamente em troca de maior exposição aos anúncios.
 */
export function DeferredAnalytics() {
  useEffect(() => {
    let analyticsArmed = false;

    const injectAnalytics = () => {
      if (analyticsArmed) return;
      analyticsArmed = true;

      // Google Analytics 4
      const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID || "G-QD71TFW6CB";
      const isValidGaId =
        gaId && typeof gaId === "string" && gaId.startsWith("G-") && gaId !== "G-WEKKHBNWZQ";

      if (isValidGaId) {
        const ga = document.createElement("script");
        ga.async = true;
        ga.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        document.head.appendChild(ga);
        const gaInit = document.createElement("script");
        gaInit.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config','${gaId}');`;
        document.head.appendChild(gaInit);
      } else {
        if (import.meta.env.DEV) {
          console.warn(
            `[DeferredAnalytics] Google Analytics loading skipped: VITE_GA_MEASUREMENT_ID is missing or set to the invalid value "${gaId}".`,
          );
        }
      }

      // Ahrefs analytics
      const ah = document.createElement("script");
      ah.async = true;
      ah.src = "https://analytics.ahrefs.com/analytics.js";
      ah.setAttribute("data-key", "bfZ001/mM2ZATYN94AAAYQ");
      document.head.appendChild(ah);
    };

    // Analytics can be loaded on idle, timeout, or first interaction
    const ric: number | null =
      "requestIdleCallback" in window
        ? (
            window as Window & {
              requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number;
            }
          ).requestIdleCallback(injectAnalytics, { timeout: 4000 })
        : null;
    const timer = window.setTimeout(injectAnalytics, 3500);

    const interactionEvents: (keyof WindowEventMap)[] = [
      "pointerdown",
      "keydown",
      "scroll",
      "touchstart",
    ];

    // Bind analytics to interactions
    interactionEvents.forEach((e) =>
      window.addEventListener(e, injectAnalytics, { once: true, passive: true }),
    );

    return () => {
      if (ric !== null && "cancelIdleCallback" in window) {
        (window as Window & { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(ric);
      }
      window.clearTimeout(timer);
      interactionEvents.forEach((e) => {
        window.removeEventListener(e, injectAnalytics);
      });
    };
  }, []);

  return null;
}
